import React, { PureComponent } from 'react';
import glamorous from 'glamorous';
import { injectIntl } from 'react-intl';
import { sortBy, reduce, get } from 'lodash';

import * as d3 from 'd3';
import { createTooltip } from '../helpers';

class PieChart extends PureComponent {
  constructor(props) {
    super(props);
    this.chartRef = null;
  }

  componentDidMount() {
    buildChart(this.props, this.chartRef);
  }

  render() {
    return (
      <glamorous.Div
        innerRef={r => this.chartRef = r}
        className="d3-chart"
        css={{
          height: this.props.isFullScreen ? '100%' : 400,
          width: '100% !important',
        }}
      />
    );
  }
}

export default injectIntl(PieChart);


function buildChart(props, element) {
  const { intl, isFullScreen, goToSearchPage } = props;

  const data = sortBy(reduce(props.dataset.data, (agg, item) => {
    const valueNum = parseInt(item.recallCount, 10);
    if (!isNaN(valueNum)) {
      agg.push({ ...item, recallCount: valueNum });
    }
    return agg;
  }, []), ['value']);

// chart dimensions
  const width = element.offsetWidth;
  const height = element.offsetHeight;
  const smallChart = width < 500;

// a circle chart needs a radius
  const radius = isFullScreen
    ? Math.min(width, height) / 2
    : Math.min(width, height) / (smallChart ? 3.5 : 2.4);

// legend dimensions
  const legendRectSize = isFullScreen ? 25 : 15; // defines the size of the colored squares in legend
  const legendSpacing = isFullScreen ? 6 : 4; // defines spacing between squares

// define color scale
  const color = d3.scaleOrdinal(d3.schemeCategory20c);
// more color scales: https://bl.ocks.org/pstuffa/3393ff2711a53975040077b7453781a9
  const gTranslate = isFullScreen ?  `translate(${width / 2}, ${height / 2})` : `translate(${radius}, ${height / 2})`;
  const svg = d3.select(element) // select element in the DOM with id 'chart'
    .append('svg') // append an svg element to the element we've selected
    .attr('width', width) // set the width of the svg element we just added
    .attr('height', height) // set the height of the svg element we just added
    .append('g') // append 'g' element to the svg element
    .attr('transform', gTranslate); // our reference is now to the 'g' element. centerting the 'g' element to the svg element

  const arc = d3.arc()
    .innerRadius(0) // none for pie chart
    .outerRadius(radius); // size of overall chart

  const pie = d3.pie() // start and end angles of the segments
    .value(d => d.recallCount) // how to extract the numerical data from each entry in our dataset
    .sort(null); // by default, data sorts in oescending value. this will mess with our animation so we set it to null

  data.forEach(d => {
    d.recallCount = +d.recallCount; // calculate count as we iterate through the data
    d.enabled = true; // add enabled property to track which entries are checked
  });

// creating the chart
  let path = svg.selectAll('path') // select all path elements inside the svg. specifically the 'g' element. they don't exist yet but they will be created below
    .data(pie(data)) //associate dataset wit he path elements we're about to create. must pass through the pie function. it magically knows how to extract values and bakes it into the pie
    .enter() //creates placeholder nodes for each of the values
    .append('path') // replace placeholders with path elements
    .attr('d', arc) // define d attribute with arc function above
    .attr('fill', d => color(d.data.label)) // use color scale to define fill of each label in dataset
    .each(function (d) {
      this._current - d;
    }); // creates a smooth animation for each track

// define tooltip
  const tip = createTooltip({ intl });
  path.call(tip);

// mouse event handlers are attached to path so they need to come after its definition
  path
    .on('mouseover', tip.show)
    .on('mousemove', tip.move)
    .on('mouseout', tip.hide)
    .on('click', d => {
      tip.hide();
      goToSearchPage(get(d, 'data.link.params'), intl);
    });

// define legend
  const legend = svg.selectAll('.legend') // selecting elements with class 'legend'
    .data(color.domain()) // refers to an array of labels from our dataset
    .enter() // creates placeholder
    .append('g') // replace placeholders with g elements
    .attr('class', 'legend') // each g is given a legend class
    .attr('transform', (d, i) => {
      const height = legendRectSize + legendSpacing; // height of element is the height of the colored square plus the spacing
      const offset = height * color.domain().length / 2; // vertical offset of the entire legend = height of a single element & half the total number of elements
      const horz = isFullScreen ? (18 * legendRectSize) : radius + 10; // the legend is shifted to the left to make room for the text
      const vert = i * height - offset; // the top of the element is hifted up or down from the center using the offset defiend earlier and the index of the current element 'i'
      return `translate(${horz}, ${vert})`; //return translation
    });

// adding colored squares to legend
  legend.append('rect') // append rectangle squares to legend
    .attr('width', legendRectSize) // width of rect size is defined above
    .attr('height', legendRectSize) // height of rect size is defined above
    .style('fill', color) // each fill is passed a color
    .style('stroke', color) // each stroke is passed a color
    .on('click', function (label) {
      const rect = d3.select(this); // this refers to the colored squared just clicked
      let enabled = true; // set enabled true to default
      const totalEnabled = d3.sum(data.map(d => { // can't disable all options
        return (d.enabled) ? 1 : 0; // return 1 for each enabled entry. and summing it up
      }));

      if (rect.attr('class') === 'disabled') { // if class is disabled
        rect.attr('class', ''); // remove class disabled
      } else { // else
        if (totalEnabled < 2) return; // if less than two labels are flagged, exit
        rect.attr('class', 'disabled'); // otherwise flag the square disabled
        enabled = false; // set enabled to false
      }

      pie.value(d => {
        if (d.label === label) d.enabled = enabled; // if entry label matches legend label
        return (d.enabled) ? d.recallCount : 0; // update enabled property and return count or 0 based on the entry's status
      });

      path = path.data(pie(data)); // update pie with new data

      path.transition() // transition of redrawn pie
        .duration(750) //
        .attrTween('d', function (d) { // 'd' specifies the d attribute that we'll be animating
          const interpolate = d3.interpolate(this._current, d); // this = current path element
          this._current = interpolate(0); // interpolate between current value and the new value of 'd'
          return t => arc(interpolate(t));
        });
    });

// adding text to legend
  legend.append('text')
    .attr('x', legendRectSize + legendSpacing)
    .attr('y', legendRectSize - legendSpacing)
    .text(d => d); // return label
}



// WEBPACK FOOTER //
// ./src/views/dashboard/chart/pie-chart/index.js